import { supabase } from '../supabaseClient';
import { captureConnectionsEvent } from './captureConnectionsEvent';
import { resolveMostRecentWorkspace } from './resolveMostRecentWorkspace';

/** In-flight ensureHomeWorkspace promises keyed by userId (StrictMode / multi-tab). */
const ensureHomeInFlight = new Map();

/**
 * Resume the user's most recent workspace, or create "My workspace" if none.
 * @returns {Promise<{ workspace: { id: string, name: string }, created: boolean }>}
 */
export const ensureHomeWorkspace = async ({ userId }) => {
  if (!userId) throw new Error('userId is required');

  const existing = ensureHomeInFlight.get(userId);
  if (existing) return existing;

  const promise = (async () => {
    const mostRecent = await resolveMostRecentWorkspace(userId);
    if (mostRecent) {
      return { workspace: mostRecent, created: false };
    }

    // Re-resolve immediately before insert (another tab / tour may have created one).
    const raced = await resolveMostRecentWorkspace(userId);
    if (raced) {
      return { workspace: raced, created: false };
    }

    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('workspaces')
      .insert([
        {
          name: 'My workspace',
          user_id: userId,
          is_public: false,
          created_at: now,
          updated_at: now,
        },
      ])
      .select('id, name')
      .single();

    if (error) throw error;
    return { workspace: data, created: true };
  })();

  ensureHomeInFlight.set(userId, promise);
  try {
    return await promise;
  } finally {
    ensureHomeInFlight.delete(userId);
  }
};

/**
 * Rename a workspace.
 * @returns {Promise<object>} Updated workspace row
 */
export const renameWorkspace = async ({ workspaceId, name }) => {
  const { data, error } = await supabase
    .from('workspaces')
    .update({
      name: name.trim(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Delete a workspace (baskets cascade).
 */
export const deleteWorkspace = async ({ workspaceId }) => {
  const { error } = await supabase
    .from('workspaces')
    .delete()
    .eq('id', workspaceId);

  if (error) throw error;
};

/**
 * @returns {Promise<{ basketCount: number, topicCount: number }>}
 */
export const getWorkspaceCounts = async (workspaceId) => {
  const { data, error } = await supabase
    .from('workspaces')
    .select('baskets:baskets(count), topics:topics(count)')
    .eq('id', workspaceId)
    .single();

  if (error) throw error;

  return {
    basketCount: data?.baskets?.[0]?.count ?? 0,
    topicCount: data?.topics?.[0]?.count ?? 0,
  };
};

/**
 * Duplicate a workspace. Calls onProgress(percent, statusText) during the run.
 * @returns {Promise<object>} New workspace row
 */
export const duplicateWorkspace = async ({
  workspace,
  userId,
  includeBaskets,
  onProgress,
}) => {
  const report = (percent, statusText) => {
    onProgress?.(percent, statusText);
  };

  report(0, 'Creating new workspace...');

  const { data: newWorkspace, error: workspaceError } = await supabase
    .from('workspaces')
    .insert([{
      name: `${workspace.name} (Copy)`,
      user_id: userId,
      is_public: false,
      exam_date: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }])
    .select()
    .single();

  if (workspaceError) throw workspaceError;

  report(20, 'Fetching topics and concepts...');

  const { data: originalTopics, error: topicsError } = await supabase
    .from('topics')
    .select(`
      *,
      concepts (*),
      tags (
        id,
        name,
        color
      )
    `)
    .eq('workspace_id', workspace.id)
    .order('order_index');

  if (topicsError) throw topicsError;

  report(20, 'Creating tags...');

  const tagsToCreate = (originalTopics || [])
    .filter((topic) => topic.tags)
    .map((topic) => ({
      name: topic.tags.name,
      color: topic.tags.color,
      workspace_id: newWorkspace.id,
      created_by: userId,
      created_at: new Date().toISOString(),
    }));

  const uniqueTags = tagsToCreate.filter(
    (tag, index, self) => index === self.findIndex((t) => t.name === tag.name)
  );

  let tagMapping = {};
  if (uniqueTags.length > 0) {
    const { data: createdTags, error: createTagsError } = await supabase
      .from('tags')
      .insert(uniqueTags)
      .select();

    if (createTagsError) throw createTagsError;

    tagMapping = createdTags.reduce(
      (acc, tag) => ({
        ...acc,
        [tag.name]: tag.id,
      }),
      {}
    );
  }

  report(40, includeBaskets ? 'Creating baskets...' : 'Creating topics...');

  let basketMapping = {};
  if (includeBaskets) {
    const { data: originalBaskets, error: basketsError } = await supabase
      .from('baskets')
      .select('*')
      .eq('workspace_id', workspace.id)
      .order('order_index');

    if (basketsError) throw basketsError;

    if (originalBaskets?.length > 0) {
      const newBaskets = originalBaskets.map((basket) => ({
        name: basket.name,
        description: basket.description,
        workspace_id: newWorkspace.id,
        order_index: basket.order_index,
        is_locked: Boolean(basket.is_locked),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));

      const { data: createdBaskets, error: createBasketsError } = await supabase
        .from('baskets')
        .insert(newBaskets)
        .select();

      if (createBasketsError) throw createBasketsError;

      basketMapping = originalBaskets.reduce(
        (acc, basket, index) => ({
          ...acc,
          [basket.id]: createdBaskets[index].id,
        }),
        {}
      );
    }
  }

  report(60, 'Creating topics...');

  if (originalTopics?.length > 0) {
    const newTopics = originalTopics.map((topic) => ({
      name: topic.name,
      relation_description: topic.relation_description,
      basket_id: includeBaskets ? basketMapping[topic.basket_id] : null,
      workspace_id: newWorkspace.id,
      user_id: userId,
      tag_id: topic.tags ? tagMapping[topic.tags.name] : null,
      order_index: topic.order_index,
      is_locked: Boolean(topic.is_locked),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    const { data: createdTopics, error: createTopicsError } = await supabase
      .from('topics')
      .insert(newTopics)
      .select();

    if (createTopicsError) throw createTopicsError;

    report(80, 'Creating concepts...');

    const topicMapping = {};
    originalTopics.forEach((oldTopic, index) => {
      topicMapping[oldTopic.id] = createdTopics[index].id;
    });

    const allConcepts = originalTopics.flatMap((topic) =>
      topic.concepts.map((concept) => ({
        text: concept.text,
        topic_id: topicMapping[topic.id],
        is_highlighted: concept.is_highlighted,
        order_index: concept.order_index,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }))
    );

    if (allConcepts.length > 0) {
      const { error: createConceptsError } = await supabase
        .from('concepts')
        .insert(allConcepts);

      if (createConceptsError) throw createConceptsError;
    }
  }

  report(100, 'Finishing up...');
  return newWorkspace;
};

/**
 * Import a public template workspace for a user.
 * @param {{ workspace: object, userId: string, source?: 'sidebar' }} params
 * @returns {Promise<{ newWorkspace: object, basketsCloned: number }>}
 */
export const importPublicWorkspace = async ({ workspace, userId, source }) => {
  const { data: newWorkspace, error: workspaceError } = await supabase
    .from('workspaces')
    .insert([{
      name: `${workspace.name} (Imported)`,
      user_id: userId,
      is_public: false,
      category: workspace.category,
      exam_date: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }])
    .select()
    .single();

  if (workspaceError) throw workspaceError;

  const { data: originalBaskets, error: basketsError } = await supabase
    .from('baskets')
    .select('*')
    .eq('workspace_id', workspace.id)
    .order('order_index');

  if (basketsError) throw basketsError;

  const basketMapping = {};
  if (originalBaskets?.length > 0) {
    const newBaskets = originalBaskets.map((basket) => ({
      name: basket.name,
      description: basket.description,
      workspace_id: newWorkspace.id,
      order_index: basket.order_index,
      is_locked: Boolean(basket.is_locked),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    const { data: createdBaskets, error: createBasketsError } = await supabase
      .from('baskets')
      .insert(newBaskets)
      .select();

    if (createBasketsError) throw createBasketsError;

    originalBaskets.forEach((basket, index) => {
      basketMapping[basket.id] = createdBaskets[index].id;
    });
  }

  const { data: originalTopics, error: topicsError } = await supabase
    .from('topics')
    .select(`
      *,
      concepts (*),
      tags (*)
    `)
    .eq('workspace_id', workspace.id)
    .order('order_index');

  if (topicsError) throw topicsError;

  if (originalTopics?.length > 0) {
    const tagsToCreate = originalTopics
      .filter((topic) => topic.tags)
      .map((topic) => ({
        name: topic.tags.name,
        color: topic.tags.color,
        workspace_id: newWorkspace.id,
        created_by: userId,
        created_at: new Date().toISOString(),
      }));

    const uniqueTags = tagsToCreate.filter(
      (tag, index, self) => index === self.findIndex((t) => t.name === tag.name)
    );

    let tagMapping = {};
    if (uniqueTags.length > 0) {
      const { data: createdTags, error: createTagsError } = await supabase
        .from('tags')
        .insert(uniqueTags)
        .select();

      if (createTagsError) throw createTagsError;

      tagMapping = createdTags.reduce(
        (acc, tag) => ({
          ...acc,
          [tag.name]: tag.id,
        }),
        {}
      );
    }

    const newTopics = originalTopics.map((topic) => ({
      name: topic.name,
      relation_description: topic.relation_description,
      basket_id: basketMapping[topic.basket_id],
      workspace_id: newWorkspace.id,
      user_id: userId,
      tag_id: topic.tags ? tagMapping[topic.tags.name] : null,
      order_index: topic.order_index,
      is_locked: Boolean(topic.is_locked),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }));

    const { data: createdTopics, error: createTopicsError } = await supabase
      .from('topics')
      .insert(newTopics)
      .select();

    if (createTopicsError) throw createTopicsError;

    const topicMapping = {};
    originalTopics.forEach((topic, index) => {
      topicMapping[topic.id] = createdTopics[index].id;
    });

    const allConcepts = originalTopics.flatMap((topic) =>
      topic.concepts.map((concept) => ({
        text: concept.text,
        topic_id: topicMapping[topic.id],
        is_highlighted: concept.is_highlighted,
        order_index: concept.order_index,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }))
    );

    if (allConcepts.length > 0) {
      const { error: createConceptsError } = await supabase
        .from('concepts')
        .insert(allConcepts);

      if (createConceptsError) throw createConceptsError;
    }
  }

  const { error: importError } = await supabase
    .from('workspace_imports')
    .insert([{
      user_id: userId,
      original_workspace_id: workspace.id,
      imported_workspace_id: newWorkspace.id,
      imported_at: new Date().toISOString(),
    }]);

  if (importError) throw importError;

  await supabase.rpc('increment_download_count', {
    workspace_id: workspace.id,
  });

  const basketsCloned = originalBaskets?.length || 0;
  const lockedBasketsCount = (originalBaskets || []).filter((b) =>
    Boolean(b.is_locked)
  ).length;
  const lockedItemsCount = (originalTopics || []).filter((t) =>
    Boolean(t.is_locked)
  ).length;

  captureConnectionsEvent('workspace_template_imported', {
    workspace_id: newWorkspace.id,
    source_workspace_id: workspace.id,
    category: workspace.category ?? null,
    baskets_cloned_count: basketsCloned,
    locked_baskets_count: lockedBasketsCount,
    locked_items_count: lockedItemsCount,
    // Phase 9 will set true when non-premium imports only is_preview baskets
    is_gated: false,
    ...(source ? { source } : {}),
  });

  return {
    newWorkspace,
    basketsCloned,
    lockedBasketsCount,
    lockedItemsCount,
  };
};
